import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pw9213Api, F913, F9131 } from '../../../services/pw9213Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './usePW9213Mode';

export function usePW9213Record() {
  const [record, setRecord] = useState<F913 | null>(null);
  const [formData, setFormData] = useState<Partial<F913>>({});
  const [details, setDetails] = useState<Partial<F9131>[]>([]);
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by composite key */
  const loadRecord = useCallback(async (empCode: string, year: string): Promise<F913 | null> => {
    setLoading(true);
    try {
      const data = await pw9213Api.get(empCode, year);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback(<K extends keyof F913>(key: K, value: F913[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback(<K extends keyof F913>(key: K): F913[K] | string => {
    return (formData as Partial<F913>)[key] ?? '';
  }, [formData]);

  /** Update a detail row field */
  const updateDetailField = useCallback((rowIdx: number, field: keyof F9131, value: F9131[keyof F9131]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[rowIdx]) {
        next[rowIdx] = { ...next[rowIdx], [field]: value };
      }
      return next;
    });
  }, []);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F913) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as F913,
        details: details as F9131[],
      };
      if (mode === 'add') {
        const result = await pw9213Api.create(req);
        message.success(`新增成功: ${result.f913_01} / ${result.f913_02}`);
        onSuccess(result);
      } else {
        const result = await pw9213Api.update(formData.f913_01!, formData.f913_02!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pw9213Api.delete(record.f913_01, record.f913_02);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row */
  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        f9131_01: formData.f913_01 || '',
        f9131_02: formData.f913_02 || '',
        f9131_03: '',
        f9131_11: 0,
        f9131_111: 0,
        f9131_12: null,
        f9131_13: 0,
        f9131_131: 0,
        f9131_14: 0,
        f9131_141: 0,
        f9131_91: '',
        f9131_92: null,
        f9131_93: '',
      },
    ]);
  }, [formData.f913_01, formData.f913_02]);

  /** Delete a detail row by index */
  const deleteDetailRow = useCallback((rowIdx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== rowIdx));
  }, []);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      f913_11: 0,
      f913_111: 0,
      f913_12: 0,
      f913_121: 0,
      f913_21: 0,
      f913_211: 0,
    });
    setDetails([]);
  }, [clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: F913) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    updateDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
